/**
 * Field help text and tooltip content for all configuration form fields.
 * BR-13: Every field must have a plain English description accessible via a ? tooltip.
 * Content sourced from the Admin Portal Attribute Definitions document.
 */

/**
 * Tooltip descriptions keyed by field key.
 * Used by GuidedForm to render ? help icons next to field labels.
 */
export const FIELD_TOOLTIPS: Record<string, string> = {
  // Business Hours
  businessHoursType: 'The type of business hours schedule. Currently only "default" is supported.',
  timeZone: 'The timezone used to calculate when business hours are active. Must be a valid IANA timezone identifier.',
  monday_enabled: 'Whether the contact centre is open on Mondays.',
  monday_startTime: 'The time the contact centre opens on Monday (24-hour format).',
  monday_endTime: 'The time the contact centre closes on Monday (24-hour format).',
  tuesday_enabled: 'Whether the contact centre is open on Tuesdays.',
  tuesday_startTime: 'The time the contact centre opens on Tuesday.',
  tuesday_endTime: 'The time the contact centre closes on Tuesday.',
  wednesday_enabled: 'Whether the contact centre is open on Wednesdays.',
  wednesday_startTime: 'The time the contact centre opens on Wednesday.',
  wednesday_endTime: 'The time the contact centre closes on Wednesday.',
  thursday_enabled: 'Whether the contact centre is open on Thursdays.',
  thursday_startTime: 'The time the contact centre opens on Thursday.',
  thursday_endTime: 'The time the contact centre closes on Thursday.',
  friday_enabled: 'Whether the contact centre is open on Fridays.',
  friday_startTime: 'The time the contact centre opens on Friday.',
  friday_endTime: 'The time the contact centre closes on Friday.',
  saturday_enabled: 'Whether the contact centre is open on Saturdays.',
  saturday_startTime: 'The time the contact centre opens on Saturday.',
  saturday_endTime: 'The time the contact centre closes on Saturday.',
  sunday_enabled: 'Whether the contact centre is open on Sundays.',
  sunday_startTime: 'The time the contact centre opens on Sunday.',
  sunday_endTime: 'The time the contact centre closes on Sunday.',

  // DNIS
  welcomeMessage: 'The audio message played to the caller when they first connect. This is the first thing the customer hears.',
  destinationType: 'Where the call should be routed after the welcome message. Choose the type of destination.',
  destinationValue: 'The specific destination to route the call to. What appears here depends on the Destination Type selected above.',
  pollyVoice: 'The text-to-speech voice used to read messages to the caller. Different voices have different accents and genders.',
  callRecordingEnabled: 'When enabled, all calls to this number will be recorded for quality and compliance purposes.',
  contactLensEnabled: 'When enabled, Amazon Contact Lens will transcribe and analyse calls in real time.',
  contactLensRedaction: 'When enabled, sensitive information (like credit card numbers) will be automatically redacted from transcripts.',
  emergencyEnabled: 'When turned on, all calls are immediately handled by the emergency action instead of normal routing. Use this during outages or incidents.',
  emergencyMessage: 'The message played to callers when Emergency Mode is active. Should explain why normal service is unavailable.',
  emergencyAction: 'What happens to the call when Emergency Mode is active.',
  outageEnabled: 'When turned on, the outage message is played and the outage action is taken for all calls.',
  outageMessage: 'The message played to callers during a system outage.',
  outageAction: 'What happens to the call during an outage.',
  businessHoursSchedule: 'The business hours configuration that determines when this number/queue is open. Calls outside these hours trigger the After Hours action.',
  afterHoursMessage: 'The message played to callers who ring outside business hours.',
  afterHoursAction: 'What happens to the call when it arrives outside business hours.',
  holidaySchedule: 'The holiday/closure configuration that defines when this number/queue is closed for holidays.',
  holidayMessage: 'The message played to callers during a holiday closure.',
  holidayAction: 'What happens to the call during a holiday.',
  blockedCallerEnabled: 'When enabled, calls from blocked numbers will be handled differently (e.g., disconnected or given a message).',
  blockedCallerConfig: 'The blocked caller list configuration to check incoming numbers against.',
  blockedCallerMessage: 'The message played to a caller whose number is on the blocked list.',
  externalTransferNumber: 'The phone number to transfer calls to when any action is set to "Transfer". Must be in international format (e.g., +6498896849).',
  repeatCallerEnabled: 'When enabled, the system checks if the caller has contacted recently and can offer priority routing.',
  repeatCallerRange: 'How far back (in days) to look for previous contacts from the same caller.',
  customerProfileEnabled: 'When enabled, the system retrieves customer information to present to the agent before connecting the call.',
  lastAgentEnabled: 'When enabled, the system attempts to connect the caller to the same agent they spoke to previously.',
  lastAgentRange: 'How far back (in days) to look for the caller\'s last agent interaction.',
  lastAgentMessage: 'The message played asking the caller if they want to be connected to the same agent as last time.',
  tagAttributesToCTR: 'Contact attributes to tag to the Contact Trace Record. Used for reporting and analytics.',

  // Menus
  menuPrompt: 'The audio message played to the caller listing their options. For example: "Press 1 for Sales, Press 2 for Support."',
  option1Intent: 'Where the call goes when the caller presses 1.',
  option2Intent: 'Where the call goes when the caller presses 2.',
  option3Intent: 'Where the call goes when the caller presses 3.',
  option4Intent: 'Where the call goes when the caller presses 4.',
  option5Intent: 'Where the call goes when the caller presses 5.',
  option6Intent: 'Where the call goes when the caller presses 6.',
  option7Intent: 'Where the call goes when the caller presses 7.',
  option8Intent: 'Where the call goes when the caller presses 8.',

  // Intents
  conditionalExpression: 'A condition that must be true for this intent to activate. If left empty, the intent always activates. Format: attribute=value',
  intentLoopDetectionThreshold: 'How many times the same intent can be triggered in a row before the system considers it a loop and escapes.',
  ruleLoopDetectionThreshold: 'How many times the same rule can be evaluated in a row before the system considers it a loop and escapes.',
  intentLoopEscapeDestinationType: 'Where to send the call if an intent loop is detected.',
  intentLoopEscapeDestinationValue: 'The specific destination for loop escape routing.',
  ruleLoopEscapeDestinationType: 'Where to send the call if a rule loop is detected.',
  ruleLoopEscapeDestinationValue: 'The specific destination for rule loop escape routing.',

  // Queues
  stepByStepGuideEnabled: 'When enabled, agents see a custom step-by-step guide view when handling calls from this queue.',
  bypassMandatoryMessagingForTransfers: 'When enabled, mandatory messages are skipped for transferred calls.',

  // Deflections
  deflectionMessage: 'The message offering the caller a digital self-service option instead of waiting in the queue.',
  chatDeflectionMessage: 'The deflection message shown specifically for the chat channel.',
  bypassDeflectionMessage: 'When enabled, skips the deflection message and goes straight to agent transfer.',
  failedDeflectionAction: 'What happens if the caller declines the deflection offer.',
  failedDeflectionValue: 'The specific destination if deflection fails.',
  chatFailedDeflectionAction: 'What happens if the chat customer declines the deflection offer.',
  chatFailedDeflectionValue: 'The specific destination if chat deflection fails.',

  // Chats
  mainMenuPrompt: 'The main menu message shown to chat customers.',
  queueName: 'The queue that handles chat requests when an agent is needed.',

  // Emails
  emailTemplate: 'The email template used for acknowledgement messages sent to customers.',
  emailPreQueueTemplate: 'The email template used for pre-queue acknowledgement messages.',
  emailTag: 'A flexible tag to categorise this email destination for reporting.',

  // Holidays
  startDate: 'The first day of the holiday or closure period.',
  endDate: 'The last day of the holiday or closure period.',
  message: 'The message played or displayed to customers during this closure.',
  action: 'What happens to calls/contacts during this holiday period.',

  // Blocked
  blockedEntries: 'The list of blocked phone numbers or keywords. Calls or messages matching these will be handled according to the blocked caller settings.',

  // DNIS Mapping
  targetDnisConfig: 'The DNIS configuration to route to based on the dialled number mapping.',
  areaCode: 'The international dialling code prefix for this number.',
  dnisTag: 'A flexible tag to categorise this inbound number for reporting.',
  agentGreeting: 'A custom greeting message for agents when they answer calls from this number.',
  intent: 'The default routing intent for calls to this number.',
};

/**
 * Dropdown option descriptions for fields where values aren't self-explanatory.
 * BR-13: Each option must show a brief description beneath its label.
 */
export const OPTION_DESCRIPTIONS: Record<string, Record<string, string>> = {
  destinationType: {
    queue: 'Route to a call queue',
    menu: 'Play an IVR menu',
    deflect: 'Offer digital self-service',
    transfer: 'Transfer to a phone number',
    disconnect: 'End the call',
    agent: 'Route to a specific agent queue',
  },
  afterHoursAction: {
    resume: 'Continue the call as normal',
    disconnect: 'End the call immediately',
    transfer: 'Send the call to the external transfer number',
  },
  emergencyAction: {
    resume: 'Continue the call as normal',
    disconnect: 'End the call immediately',
    transfer: 'Send the call to the external transfer number',
  },
  holidayAction: {
    resume: 'Continue the call as normal',
    disconnect: 'End the call immediately',
    transfer: 'Send the call to the external transfer number',
  },
  outageAction: {
    resume: 'Continue the call as normal',
    disconnect: 'End the call immediately',
  },
  failedDeflectionAction: {
    menu: 'Return to the IVR menu',
    queue: 'Place in a call queue',
    disconnect: 'End the call',
    deflect: 'Try deflection again',
    transfer: 'Transfer to a phone number',
  },
};

/**
 * Polly voice options with language, gender, and flag for display.
 * BR-13: Aria pinned to top for NZ deployments.
 */
export const POLLY_VOICE_OPTIONS: { label: string; value: string; description: string }[] = [
  { label: 'Aria', value: 'Aria', description: '🇳🇿 English (New Zealand), Female' },
  { label: 'Olivia', value: 'Olivia', description: '🇳🇿 English (New Zealand), Female' },
  { label: 'Russell', value: 'Russell', description: '🇦🇺 English (Australia), Male' },
  { label: 'Nicole', value: 'Nicole', description: '🇦🇺 English (Australia), Female' },
  { label: 'Emma', value: 'Emma', description: '🇬🇧 English (UK), Female' },
  { label: 'Brian', value: 'Brian', description: '🇬🇧 English (UK), Male' },
  { label: 'Joanna', value: 'Joanna', description: '🇺🇸 English (US), Female' },
  { label: 'Kimberly', value: 'Kimberly', description: '🇺🇸 English (US), Female' },
  { label: 'Kendra', value: 'Kendra', description: '🇺🇸 English (US), Female' },
  { label: 'Salli', value: 'Salli', description: '🇺🇸 English (US), Female' },
  { label: 'Joey', value: 'Joey', description: '🇺🇸 English (US), Male' },
  { label: 'Justin', value: 'Justin', description: '🇺🇸 English (US), Male' },
];

/**
 * Inline format hints shown beneath specific field types.
 * BR-13: Help users understand expected input format.
 */
export const FORMAT_HINTS: Record<string, string> = {
  time: 'e.g. 08:00 for 8am, 17:30 for 5:30pm',
  e164: 'Enter in international format, e.g. +6498896849',
  configId: 'Lowercase letters, numbers and hyphens only, e.g. claims-queue-hours',
  conditionalExpression: 'Format: attribute=value, e.g. pollyVoice=Aria',
};
